import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import InterviewAssistantPane from './panes/InterviewAssistantPane';
import MockInterviewPane from './panes/MockInterviewPane';
import InterviewQuestionsPane from './panes/InterviewQuestionsPane';
import ResumeBuilderPane from './panes/ResumeBuilderPane';
import CoverLetterPane from './panes/CoverLetterPane';
import ATSCheckPane from './panes/ATSCheckPane';
import AutoJobApplyPane from './panes/AutoJobApplyPane';
import LinkedInOptimizerPane from './panes/LinkedInOptimizerPane';
import PortfolioBuilderPane from './panes/PortfolioBuilderPane';
import CertificationPane from './panes/CertificationPane';
import PlaceholderPane from './panes/PlaceholderPane';
import AuthModal from '../components/AuthModal';
import BillingModal from '../components/BillingModal';
import { useUser } from '../contexts/UserContext';
import {
  MicIcon,
  BeakerIcon,
  HelpIcon,
  FileTextIcon,
  MailIcon,
  CheckCircleIcon,
  RocketIcon,
  BriefcaseIcon,
  AwardIcon,
  LayoutGridIcon,
  LockIcon,
  MenuIcon,
} from './icons';
import './ElectronShell.css';
import './interview-overrides.css';
import './profile-overrides.css';
import './auto-apply-overrides.css';
import './cert-overrides.css';
import './dashboard-overrides.css';

const SIDEBAR_PREF_KEY = 'terview.electron.sidebarCollapsed';

/**
 * ElectronShell — top-level layout for the Electron build.
 *
 * Sidebar with the full feature list on the left, active pane content on
 * the right. Lives outside the React Router's <Routes> tree so we can show
 * a unified shell while the actual feature flows still navigate normally
 * (the live interview UI, billing flow, payment success, etc. continue to
 * be reached by route changes — we just wrap everything in this chrome).
 *
 * Activation: rendered by App.js only when isElectron() === true. When the
 * route is one of the SESSION_ROUTES (a feature is mid-flow), we render
 * the routed page full-bleed without the sidebar so the feature has the
 * full window — same UX as LockedIn AI when a meeting is live.
 */

// Routes where we hide the sidebar and let the routed page take the whole
// window. Includes the live "session" screens (where we never want chrome
// in the way) AND the existing landing/feature pages that placeholders
// hand off to until each pane is ported. Once a feature is fully ported
// to its own pane, drop its landing route from this list.
const SESSION_ROUTES = [
  // Live session UIs — must be full-bleed always.
  '/interview-session',
  '/mock-interview-session',
  '/cover-letter-session',
  '/interview-questions-session',
  '/ats-check-session',
  '/ats-studio',
  '/linkedin-optimizer-session',
  '/auto-apply-session',
  '/certification-session',
  '/portfolio-session',
  '/floating',
  '/onboarding',
  '/payment-success',
  // Existing landings the placeholder panes still hand off to.
  '/InterviewAssistant',
  '/mockInterview',
  '/InterviewQuestions',
  '/resume-builder',
  '/CoverLetter',
  '/ATSCheck',
  '/AutoJobApply',
  '/LinkedInOptimizer',
  '/certification',
  '/portfolio',
];

// Routes that render INSIDE the shell — sidebar stays visible, the routed
// page lives in the content area. Used for app-y pages reachable from the
// account menu (Profile, Dashboard, Billing redirect target). Without
// this list these pages would either go full-bleed (no sidebar = the user
// gets stranded) OR get hidden behind the active pane.
const IN_SHELL_ROUTES = [
  '/profile',
  '/Dashboard',
];

// The sidebar feature list. Each entry maps to either a desktop pane (a
// component imported above) or a fallback placeholder that hands off to
// the existing web route. As we port each feature to its own pane we
// replace the `Component: PlaceholderPane` row with the real one.
const PANES = [
  {
    key: 'interview',
    label: 'AI Interview Assistant',
    Icon: MicIcon,
    Component: InterviewAssistantPane,
  },
  {
    key: 'mock-interview',
    label: 'Mock Interview',
    Icon: BeakerIcon,
    Component: MockInterviewPane,
  },
  {
    key: 'interview-questions',
    label: 'Interview Questions',
    Icon: HelpIcon,
    Component: InterviewQuestionsPane,
  },
  {
    key: 'resume-builder',
    label: 'Resume Builder',
    Icon: FileTextIcon,
    Component: ResumeBuilderPane,
  },
  {
    key: 'cover-letter',
    label: 'Cover Letter',
    Icon: MailIcon,
    Component: CoverLetterPane,
  },
  {
    key: 'ats-check',
    label: 'ATS Check',
    Icon: CheckCircleIcon,
    Component: ATSCheckPane,
  },
  {
    key: 'auto-apply',
    label: 'Auto Job Apply',
    Icon: RocketIcon,
    Component: AutoJobApplyPane,
  },
  {
    key: 'linkedin',
    label: 'LinkedIn Optimizer',
    Icon: BriefcaseIcon,
    Component: LinkedInOptimizerPane,
  },
  {
    key: 'certification',
    label: 'Certifications',
    Icon: AwardIcon,
    Component: CertificationPane,
  },
  {
    key: 'portfolio',
    label: 'Portfolio Builder',
    Icon: LayoutGridIcon,
    Component: PortfolioBuilderPane,
  },
];

export default function ElectronShell({ children }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, loading: userLoading } = useUser();
  const [activePaneKey, setActivePaneKey] = useState('interview');
  const [authOpen, setAuthOpen] = useState(false);
  const [billingOpen, setBillingOpen] = useState(false);

  // Tag <body> so interview-overrides.css can scope to body.electron-app.
  // Keeps website users on the original look even when both stylesheets
  // are bundled (CRA imports are global by default).
  useEffect(() => {
    document.body.classList.add('electron-app');
    return () => document.body.classList.remove('electron-app');
  }, []);

  // Shared "open pricing" channel — panes that need an upsell flow (Resume
  // Builder out-of-credits, Portfolio Builder gated share link, etc.) all
  // dispatch the same custom event the website uses, so we don't have to
  // thread an `onOpenBilling` prop through every pane.
  useEffect(() => {
    const onOpenPricing = () => setBillingOpen(true);
    document.addEventListener('openPricingModal', onOpenPricing);
    return () => document.removeEventListener('openPricingModal', onOpenPricing);
  }, []);
  // Sidebar collapse state — persisted across sessions in localStorage so
  // the user's preferred width sticks between launches.
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_PREF_KEY) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_PREF_KEY, isCollapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [isCollapsed]);

  // Session mode — panes (Mock Interview, AI Interview Assistant) dispatch
  // `terview:session-started` when entering an active interview view so the
  // shell can auto-collapse the sidebar and signal "you're in a test".
  // We remember the prior collapse state and restore it on session end so
  // the user's preference isn't overwritten permanently.
  const [sessionActive, setSessionActive] = useState(false);
  useEffect(() => {
    let prevCollapsed = null;
    const onStart = () => {
      setSessionActive(true);
      if (prevCollapsed === null) prevCollapsed = isCollapsed;
      if (!isCollapsed) setIsCollapsed(true);
    };
    const onEnd = () => {
      setSessionActive(false);
      if (prevCollapsed === false) setIsCollapsed(false);
      prevCollapsed = null;
    };
    window.addEventListener('terview:session-started', onStart);
    window.addEventListener('terview:session-ended', onEnd);
    return () => {
      window.removeEventListener('terview:session-started', onStart);
      window.removeEventListener('terview:session-ended', onEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Routes that should take the full window without the sidebar chrome —
  // live interview, mock interview, etc. The routed page is rendered by
  // App.js's <Routes>; we just step out of the way.
  const isSessionRoute = SESSION_ROUTES.some(r => pathname.startsWith(r));
  if (isSessionRoute) return children;

  // Auth gate — the panes show real user data and trigger paid endpoints,
  // so we require sign-in before any feature is visible. While the user
  // context is hydrating we render an empty surface to avoid the gate
  // flashing for already-signed-in users.
  if (userLoading) return <div className="es-root" />;
  if (!user) {
    // Feature bubbles that float around the central sign-in CTA. Each
    // bubble is positioned via top/left percentages so the layout reads
    // as an even halo around the pane regardless of window width. Click
    // any bubble → opens the AuthModal (same affordance as the central
    // CTA), since the user can't actually use any feature unsigned-in.
    // Eight bubbles arranged in an oval halo around the central CTA.
    // Tops capped at 76% so they stay clear of the content-area bottom
    // edge regardless of window height; outer/inner horizontal offsets
    // alternate so the ring reads as a smooth curve, not a grid.
    const SIGNIN_BUBBLES = [
      { Icon: MicIcon,         label: 'Interview\nAssistant',  top: '14%', left: '18%' },
      { Icon: BeakerIcon,      label: 'Mock\nInterview',       top: '40%', left: '8%'  },
      { Icon: FileTextIcon,    label: 'Resume\nBuilder',       top: '60%', left: '8%'  },
      { Icon: MailIcon,        label: 'Cover\nLetter',         top: '76%', left: '22%' },
      { Icon: HelpIcon,        label: 'Interview\nQuestions',  top: '14%', left: '74%' },
      { Icon: CheckCircleIcon, label: 'ATS\nCheck',            top: '40%', left: '84%' },
      { Icon: RocketIcon,      label: 'Auto Job\nApply',       top: '60%', left: '84%' },
      { Icon: BriefcaseIcon,   label: 'LinkedIn\nOptimizer',   top: '76%', left: '70%' },
    ];

    const promptSignIn = () => setAuthOpen(true);

    return (
      <div className="es-root">
        <TopBar />
        <div className="es-body">
          <main className="es-content es-signin-stage">
            {SIGNIN_BUBBLES.map((b, i) => {
              const Icon = b.Icon;
              return (
                <button
                  key={b.label}
                  type="button"
                  className="es-signin-bubble"
                  style={{
                    top: b.top,
                    left: b.left,
                    animationDelay: `${(i * 0.4).toFixed(2)}s`,
                  }}
                  onClick={promptSignIn}
                  title={`${b.label.replace(/\n/g, ' ')} — sign in to use`}
                >
                  <span className="es-signin-bubble-icon"><Icon size={22} /></span>
                  <span className="es-signin-bubble-label">{b.label}</span>
                </button>
              );
            })}
            <div className="pane es-signin-pane" style={{ textAlign: 'center', maxWidth: 440 }}>
              <div className="pane-eyebrow">
                <LockIcon size={11} /> Welcome to Terview
              </div>
              <h1 className="pane-title">Sign in to continue</h1>
              <p className="pane-subtitle" style={{ marginLeft: 'auto', marginRight: 'auto' }}>
                The desktop app uses your Terview account for resume tailoring, interview history,
                and 24/7 auto-apply. Sign in to unlock every feature.
              </p>
              <button
                type="button"
                className="pane-cta"
                onClick={promptSignIn}
              >
                Sign in →
              </button>
            </div>
          </main>
        </div>
        <AuthModal
          isOpen={authOpen}
          onClose={() => setAuthOpen(false)}
          onSuccess={() => setAuthOpen(false)}
        />
      </div>
    );
  }

  // Default: show the sidebar + either the routed page (if the URL is an
  // in-shell route like /profile, /Dashboard) or the active pane.
  const activePane = PANES.find(p => p.key === activePaneKey) || PANES[0];
  const ActiveComponent = activePane.Component;
  const isInShellRoute = IN_SHELL_ROUTES.some(r => pathname.startsWith(r));

  const toggle = () => setIsCollapsed(c => !c);

  // When the user picks a workspace pane while parked on an in-shell route
  // (e.g. /profile), we have to clear the URL — otherwise the route still
  // matches and the routed page keeps rendering instead of the new pane.
  // `/` is the website's homepage but inside Electron the shell ignores
  // the homepage route and renders the active pane.
  const handleSelectPane = (key) => {
    setActivePaneKey(key);
    if (isInShellRoute) navigate('/', { replace: true });
  };

  // Sidebar visibility is purely the user's collapse preference. We used
  // to force-show on /profile and /Dashboard out of fear the user would
  // be stranded with no way back — but the .es-hamburger affordance below
  // already lets them re-open the sidebar from any route, so the collapse
  // toggle should work uniformly on every tab (including the ones reached
  // from the account menu).
  const sidebarVisible = !isCollapsed;

  return (
    <div className={`es-root ${sidebarVisible ? '' : 'collapsed'} ${sessionActive ? 'session-active' : ''}`}>
      <TopBar />
      <div className="es-body">
        {sidebarVisible && (
          <Sidebar
            activePaneKey={isInShellRoute ? null : activePaneKey}
            onSelectPane={handleSelectPane}
            panes={PANES}
            onToggle={toggle}
            onOpenBilling={() => setBillingOpen(true)}
          />
        )}
        <main className="es-content" style={{ position: 'relative' }}>
          {!sidebarVisible && !sessionActive && (
            <button
              type="button"
              className="es-hamburger"
              onClick={toggle}
              title="Show sidebar"
              aria-label="Show sidebar"
            >
              <MenuIcon size={18} />
            </button>
          )}
          {isInShellRoute ? children : <ActiveComponent />}
        </main>
      </div>
      <BillingModal
        show={billingOpen}
        onClose={() => setBillingOpen(false)}
      />
    </div>
  );
}
